import { customAxiosInstanceMutator } from './custom-axios-instance';
import type { AddCollectionNote } from './schemas/loanServiceSchemas/addCollectionNote';
import type { CollectionNote } from './schemas/loanServiceSchemas/collectionNote';
import type { CollectionNotePage } from './schemas/loanServiceSchemas/collectionNotePage';
import type { GetCollectionNotesParams } from './schemas/loanServiceSchemas/getCollectionNotesParams';

/**
 * Add a note to a ledger account (collection note)
 * @summary Add collection note to ledger account
 */
export const addCollectionNote = (
  accountId: string,
  note: string,
  context?: string,
  createdBy?: string,
  signal?: AbortSignal,
  followUp?: CollectionFollowUp
) => {
  return customAxiosInstanceMutator<CollectionNote>(
    {
      url: `/loan/api/v1/ledger-accounts/${accountId}/notes`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: { note, ...(context ? { context } : {}), ...(createdBy ? { createdBy } : {}), ...(followUp || {}) },
      signal
    },
  );
};

/** A contact with the client, and optionally a promise to pay, recorded with a collection note (US-36). */
export type CollectionFollowUp = Pick<
  AddCollectionNote,
  'contactMethod' | 'contactPerson' | 'outcome' | 'promiseAmount' | 'promiseDate'
>;

/**
 * Retrieve notes for a ledger account (paginated, newest first)
 * @summary Get collection notes for ledger account
 */
export const getCollectionNotes = (
  accountId: string,
  params?: GetCollectionNotesParams,
  signal?: AbortSignal
) => {
  return customAxiosInstanceMutator<CollectionNotePage>(
    {
      url: `/loan/api/v1/ledger-accounts/${accountId}/notes`,
      method: 'GET',
      params,
      signal
    },
  );
};

/**
 * Retrieve notes across every ledger account of one customer (paginated, newest first); each names its loan account
 * @summary Get collection notes for a customer
 */
export const getCustomerCollectionNotes = (
  customerPartyId: string,
  params?: { page?: number; size?: number },
  signal?: AbortSignal
) => {
  return customAxiosInstanceMutator<CollectionNotePage>(
    {
      url: `/loan/api/v1/customers/${customerPartyId}/collection-notes`,
      method: 'GET',
      params,
      signal
    },
  );
};

/**
 * Types for collection note service
 */
export type AddCollectionNoteMutationResult = Awaited<ReturnType<typeof addCollectionNote>>;
export type AddCollectionNoteMutationError = Error;

export type GetCollectionNotesQueryResult = Awaited<ReturnType<typeof getCollectionNotes>>;
export type GetCollectionNotesQueryError = Error;
